import { ctaPose, easeInOutCubic, easeOutCubic, span } from "../chapter_reward_motion"
import { TITLE_SETTLED_MS } from "./title_letters"

/**
 * The chapter title card's timeline (chapter_announcement.ts), as pure functions of its clock.
 *
 * Arriving: the sky comes up out of black, the ridges dissolve in farthest first, then the words
 * one at a time -- the overline, the title catching left to right and cooling to cream, the rule
 * drawn out from the middle, the line under it -- and Begin lights up once. After that only the
 * firelight, its smoke, the moon on the water and a star or two move. Begin and Later answer from
 * the first frame; a click anywhere else on the card while it arrives jumps to the end of this.
 */
export const CARD = {
    /** The sky out of black, dithered. */
    skyMs: 320,
    /** Each ridge starts this much after the one behind it, and takes `ridgeFadeMs` to dissolve in. */
    ridgeStepMs: 110,
    ridgeFadeMs: 260,
    overlineAt: 180,
    /** The title starts to catch, and the "reveal" sound plays. */
    titleAt: 360,
    ruleAt: 640,
    ruleMs: 520,
    lineAt: 980,
    /** Begin's plate lights up from here (the checklist call to action's light-up). */
    igniteAt: 1050,
    /** Everything has arrived. */
    restMs: 1900,
    /** Begin: the words go, then the view dissolves in toward the firelight, which goes last. */
    beginWordsMs: 260,
    beginDissolveAt: 240,
    beginDissolveMs: 700,
    beginMs: 1000,
    /** Later: the card simply fades. */
    laterMs: 480,
}

/** The scene is fully in once the nearest of `layers` has dissolved in. */
export const sceneInMs = (layers: number) => CARD.skyMs * 0.4 + layers * CARD.ridgeStepMs + CARD.ridgeFadeMs

/**
 * Is a pixel of `layer` (0 the sky, then the land farthest first) in yet, for its dither value
 * `b` (0..1)? A ridge not yet in shows the sky behind it; the sky not yet in is black.
 */
export const shownAt = (ms: number, layer: number, b: number) =>
    layer === 0 ? ms > b * CARD.skyMs : ms > CARD.skyMs * 0.4 + layer * CARD.ridgeStepMs + b * CARD.ridgeFadeMs

export const cardPose = (ms: number) => ({
    overlineAlpha: easeOutCubic(span(ms, CARD.overlineAt, CARD.overlineAt + 340)),
    overlineRise: 4 * (1 - easeOutCubic(span(ms, CARD.overlineAt, CARD.overlineAt + 520))),
    titleMs: Math.min(ms - CARD.titleAt, TITLE_SETTLED_MS),
    rule: span(ms, CARD.ruleAt, CARD.ruleAt + CARD.ruleMs),
    lineAlpha: easeOutCubic(span(ms, CARD.lineAt, CARD.lineAt + 360)),
    lineRise: 5 * (1 - easeOutCubic(span(ms, CARD.lineAt, CARD.lineAt + 520))),
    /** The actions are there, unlit, from the start, so they can be answered at once. */
    actionsAlpha: easeOutCubic(span(ms, 0, 300)),
    actions: ctaPose(ms - CARD.igniteAt + 140),
    settled: ms >= CARD.restMs && ms - CARD.titleAt >= TITLE_SETTLED_MS,
})

export const beginPose = (ms: number) => ({
    words: 1 - easeOutCubic(span(ms, 0, CARD.beginWordsMs)),
    dissolve: span(ms, CARD.beginDissolveAt, CARD.beginDissolveAt + CARD.beginDissolveMs),
    done: ms >= CARD.beginMs,
})

export const laterPose = (ms: number) => ({
    words: 1 - easeOutCubic(span(ms, 0, 260)),
    cover: 1 - easeInOutCubic(span(ms, 80, CARD.laterMs)),
    done: ms >= CARD.laterMs,
})
